import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiAlertTriangle, FiCheckCircle, FiLoader, FiX, FiZap } from 'react-icons/fi';

import { databasesApi } from '@/modules/databases/services/api';
import { DatabaseType, type DatabaseResponse } from '@troia-v3/types';

import {
  useAnalyzePrompt,
  useExecuteMigration,
  useRefinePromptStructure,
} from '../../hooks/usePromptMigration';
import type { MigrationBlock } from '../../services/promptMigrationApi';
import { PromptDiffModal } from '../PromptDiffModal';
import { MigrationStepCard } from './MigrationStepCard';

type WizardStep =
  | 'loading-databases'
  | 'no-databases'
  | 'analyzing'
  | 'stepping'
  | 'offer-refine'        // ← Pergunta se quer refinar estrutura
  | 'refining'            // ← LLM gerando rewrite
  | 'refine-diff'         // ← PromptDiffModal mostrando diff
  | 'complete'
  | 'empty'
  | 'error';

export interface PromptMigrationWizardProps {
  agentId?: string;
  systemPrompt: string;
  databaseIds: string[];
  onComplete: (updatedPrompt: string) => void;
  onCancel: () => void;
}

export function PromptMigrationWizard({
  agentId,
  systemPrompt,
  databaseIds,
  onComplete,
  onCancel,
}: PromptMigrationWizardProps) {
  const [step, setStep] = useState<WizardStep>('loading-databases');
  const [blocks, setBlocks] = useState<MigrationBlock[]>([]);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState(systemPrompt);
  /**
   * Base de documentos onde TODOS os documentos gerados pela migração
   * serão criados. Single source — mesmo padrão do ResolveGapModal,
   * pra evitar o bug de "1 db nova por documento" que existia antes.
   */
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>('');

  const analyzeMutation = useAnalyzePrompt(agentId);
  const executeMutation = useExecuteMigration(agentId);
  const refineMutation = useRefinePromptStructure(agentId);

  /**
   * Resultado do `refine-prompt-structure` quando admin aceita refinar
   * a estrutura no step `offer-refine`. Guardamos o newPrompt aqui pra
   * comparar com o `currentPrompt` no `PromptDiffModal` (step `refine-diff`).
   */
  const [refinedPrompt, setRefinedPrompt] = useState<string>('');

  // Carrega databases do tenant e filtra pelas que estão vinculadas ao
  // agente E são do tipo `documents` — único tipo que aceita o shape
  // criado pela migração.
  const { data: databasesResponse, isLoading: loadingDatabases } = useQuery({
    queryKey: ['databases-for-migration', agentId, databaseIds],
    queryFn: () => databasesApi.getAll({ limit: 100 }),
    staleTime: 60 * 1000,
  });

  const documentDatabases: DatabaseResponse[] = useMemo(() => {
    if (!databasesResponse?.items) return [];
    return databasesResponse.items.filter(
      (db) =>
        db.type === DatabaseType.DOCUMENTS && databaseIds.includes(db.id),
    );
  }, [databasesResponse, databaseIds]);

  // Roteia o estado inicial com base na quantidade de databases:
  //   0 → no-databases (alerta)
  //   1+ → vai pra analyzing direto (default selecionado = primeira base;
  //         admin pode mudar a escolha em cada bloco via select inline no
  //         MigrationStepCard quando há 2+ bases)
  useEffect(() => {
    if (loadingDatabases) return;
    if (documentDatabases.length === 0) {
      setStep('no-databases');
      return;
    }
    setSelectedDatabaseId(documentDatabases[0]!.id);
    setStep('analyzing');
  }, [loadingDatabases, documentDatabases]);

  // Dispara análise quando entramos no state `analyzing` E temos um
  // database selecionado. `analyzeMutation.mutate` é estável o suficiente
  // pra deps — usamos eslint-disable porque incluir o hook completo
  // dispara re-mount no react-query.
  useEffect(() => {
    if (step !== 'analyzing' || !selectedDatabaseId) return;
    if (blocks.length > 0) return; // já analisou
    analyzeMutation.mutate(
      { systemPrompt, databaseIds },
      {
        onSuccess: (data) => {
          if (data.blocks.length === 0) {
            setStep('empty');
          } else {
            setBlocks(data.blocks);
            setStep('stepping');
          }
        },
        onError: () => {
          setStep('error');
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedDatabaseId]);

  const advanceOrComplete = useCallback(
    (updatedPrompt: string) => {
      setCurrentPrompt(updatedPrompt);
      if (currentBlockIndex + 1 >= blocks.length) {
        // Em vez de fechar o wizard imediato, oferece um passo extra
        // pra refinar a estrutura do prompt (reorganizar tags XML do
        // catálogo). Admin pode pular se quiser sair logo.
        setStep('offer-refine');
      } else {
        setCurrentBlockIndex((prev) => prev + 1);
      }
    },
    [blocks.length, currentBlockIndex],
  );

  /**
   * Admin aceitou refinar a estrutura no `offer-refine` step. Dispara
   * `useRefinePromptStructure` que chama o `PromptRewriterService` no
   * backend, e quando retorna mostra o diff via `PromptDiffModal`.
   */
  const handleAcceptRefine = useCallback(() => {
    setStep('refining');
    refineMutation.mutate(
      { currentPrompt },
      {
        onSuccess: (data) => {
          if (data.newSystemPrompt.trim() === currentPrompt.trim()) {
            // Já está bem estruturado — pula o diff e fecha
            onComplete(currentPrompt);
            return;
          }
          setRefinedPrompt(data.newSystemPrompt);
          setStep('refine-diff');
        },
        onError: () => {
          // Falhou no refinamento — fecha sem refinar (prompt já está
          // migrado e persistido pelo backend, só não foi reorganizado)
          onComplete(currentPrompt);
        },
      },
    );
  }, [currentPrompt, refineMutation, onComplete]);

  const handleSkipRefine = useCallback(() => {
    onComplete(currentPrompt);
  }, [currentPrompt, onComplete]);

  const handleAcceptRefineDiff = useCallback(() => {
    onComplete(refinedPrompt);
  }, [refinedPrompt, onComplete]);

  const handleRejectRefineDiff = useCallback(() => {
    // Admin rejeitou o diff — fica com o prompt pós-migração sem refinar
    onComplete(currentPrompt);
  }, [currentPrompt, onComplete]);

  const handleMigrate = useCallback(() => {
    const block = blocks[currentBlockIndex];
    if (!block || !selectedDatabaseId) return;

    // FORÇA o database selecionado pelo admin em TODOS os blocos —
    // backend antes criava uma db nova por documento quando o LLM não
    // sugeria databaseId (bug). Agora a UI escolhe uma só pra todos os
    // docs, e o backend usa direto sem criar db nova.
    const selectedDbName =
      documentDatabases.find((db) => db.id === selectedDatabaseId)?.name ??
      block.destination.databaseName;
    const overriddenBlock: MigrationBlock = {
      ...block,
      action: 'create_document',
      destination: {
        ...block.destination,
        databaseId: selectedDatabaseId,
        databaseName: selectedDbName,
      },
    };

    executeMutation.mutate(
      { block: overriddenBlock, currentPrompt },
      {
        onSuccess: (data) => {
          advanceOrComplete(data.updatedPrompt);
        },
      },
    );
  }, [blocks, currentBlockIndex, currentPrompt, executeMutation, advanceOrComplete, selectedDatabaseId, documentDatabases]);

  const handleKeep = useCallback(() => {
    advanceOrComplete(currentPrompt);
  }, [currentPrompt, advanceOrComplete]);

  // ─── Render states ────────────────────────────────

  if (step === 'loading-databases') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <FiLoader
            size={32}
            className="animate-spin"
            style={{ color: '#8b5cf6' }}
          />
        </div>
        <div style={{ fontSize: '13px', color: '#64748b' }}>
          Carregando bases do agente...
        </div>
      </div>
    );
  }

  if (step === 'no-databases') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <FiAlertTriangle size={32} style={{ color: '#f59e0b' }} />
        </div>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '8px' }}>
          Sem base de documentos vinculada
        </div>
        <div
          style={{
            fontSize: '13px',
            color: '#64748b',
            marginBottom: '16px',
            maxWidth: '480px',
            margin: '0 auto 16px',
            lineHeight: 1.5,
          }}
        >
          Este agente não tem nenhuma base do tipo <strong>Documentos</strong>.
          Para otimizar o prompt movendo conteúdo factual pra base, adicione
          uma base de documentos ao agente na aba <strong>Bases de dados</strong>.
        </div>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 20px',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#374151',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Fechar
        </button>
      </div>
    );
  }

  if (step === 'analyzing') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <FiLoader
            size={32}
            className="animate-spin"
            style={{ color: '#8b5cf6' }}
          />
        </div>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '4px' }}>
          Analisando seu prompt...
        </div>
        <div style={{ fontSize: '13px', color: '#64748b' }}>
          Identificando conteúdo que pode ser movido para o banco de dados
        </div>
      </div>
    );
  }

  if (step === 'empty') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ marginBottom: '16px' }}>
          <FiCheckCircle size={32} style={{ color: '#16a34a' }} />
        </div>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '4px' }}>
          Seu prompt já está otimizado!
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          Não encontramos conteúdo factual que precise ser movido para o banco de dados.
        </div>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 20px',
            borderRadius: '6px',
            border: 'none',
            background: '#8b5cf6',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Fechar
        </button>
      </div>
    );
  }

  if (step === 'offer-refine') {
    return (
      <div style={{ padding: '32px 40px', maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <FiZap size={32} style={{ color: '#8b5cf6' }} />
        </div>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '8px' }}>
          Otimização adicional disponível
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '20px', lineHeight: 1.5 }}>
          O prompt restante pode ter conteúdo em categoria errada (ex: regras
          dentro da seção de tom). Quer otimizar a estrutura do prompt em tags
          XML do catálogo? O conteúdo é preservado — só é reorganizado nas
          tags certas.
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={handleSkipRefine}
            style={{
              padding: '8px 20px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#374151',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Pular
          </button>
          <button
            type="button"
            onClick={handleAcceptRefine}
            style={{
              padding: '8px 20px',
              borderRadius: '6px',
              border: 'none',
              background: '#8b5cf6',
              color: '#fff',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Refinar estrutura
          </button>
        </div>
      </div>
    );
  }

  if (step === 'refining') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <FiLoader
            size={32}
            className="animate-spin"
            style={{ color: '#8b5cf6' }}
          />
        </div>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '4px' }}>
          Reorganizando estrutura do prompt...
        </div>
        <div style={{ fontSize: '13px', color: '#64748b' }}>
          Movendo trechos pra a tag XML correta sem perder conteúdo.
        </div>
      </div>
    );
  }

  if (step === 'refine-diff') {
    return (
      <PromptDiffModal
        isOpen={true}
        onClose={handleRejectRefineDiff}
        onConfirm={handleAcceptRefineDiff}
        originalPrompt={currentPrompt}
        newPrompt={refinedPrompt}
        mode="apply"
      />
    );
  }

  if (step === 'error') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ marginBottom: '16px' }}>
          <FiX size={32} style={{ color: '#dc2626' }} />
        </div>
        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '16px', marginBottom: '4px' }}>
          Erro ao analisar o prompt
        </div>
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          Não foi possível analisar seu prompt. Tente novamente mais tarde.
        </div>
        <button
          onClick={onCancel}
          style={{
            padding: '8px 20px',
            borderRadius: '6px',
            border: '1px solid #e2e8f0',
            background: '#fff',
            color: '#374151',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          Fechar
        </button>
      </div>
    );
  }

  const currentBlock = blocks[currentBlockIndex];
  if (!currentBlock) return null;

  return (
    <MigrationStepCard
      block={currentBlock}
      currentStep={currentBlockIndex}
      totalSteps={blocks.length}
      isMigrating={executeMutation.isPending}
      onMigrate={handleMigrate}
      onKeep={handleKeep}
      documentDatabases={documentDatabases}
      selectedDatabaseId={selectedDatabaseId}
      onChangeDatabase={setSelectedDatabaseId}
    />
  );
}
